import { TrashIcon } from "@heroicons/react/outline";
import { Editor } from "@tiptap/core";
import { isEqual } from "lodash";
import { ReactNode, useEffect, useRef, useState } from "react";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import {
  convertToTiptapParagraph,
  isEmptyValue,
} from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";
import { classNames } from "@helpers/css";
import { parseStringToJSON } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import AddButton from "./add-button";

const CriteriaItem = ({
  uuid,
  content,
  disabled,
  onDelete,
  editable,
  textAreaEnabled,
  onChange,
  onPasteMultipleCriteria,
  onEnter,
  onBlur,
  revealOnHover,
  placeholder,
}: {
  key: string | number;
  uuid?: string;
  content?: string | null;
  disabled?: boolean;
  onDelete: () => void;
  textAreaEnabled?: boolean;
  editable?: boolean;
  onChange: (content: string) => void;
  onPasteMultipleCriteria: (
    items: ReadonlyArray<{
      uuid?: string;
      content?: string | null;
    }>,
  ) => void;
  onEnter?: () => void;
  onBlur?: () => void;
  revealOnHover?: boolean;
  placeholder?: string;
}) => {
  const editorRef = useRef<Editor | null>(null);
  // The editor only reads its content when it's created (per uuid). A saved
  // criterion keeps its uuid across refetches, so text changed somewhere else,
  // e.g. in the competency modal, has to be put in by hand. Not while it's
  // being typed in: there the text on screen is the newer one.
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || editor.isDestroyed || editor.isFocused) {
      return;
    }
    const nextContent = parseStringToJSON(content) || content || "";
    const isSame =
      typeof nextContent === "string"
        ? nextContent === editor.getHTML()
        : isEqual(nextContent, editor.getJSON());
    if (!isSame) {
      // Not an edit made here, so it mustn't be reported as one (that would
      // autosave it again and mark the page unsaved).
      editor.commands.setContent(nextContent, { emitUpdate: false });
    }
  }, [content]);
  return (
    <div
      aria-label="Criteria input"
      className="flex items-start gap-2"
      onKeyDownCapture={(evt) => {
        // Enter in the editor starts the next criterion instead of a new
        // paragraph (shift+enter still breaks the line). The bin button keeps
        // its own Enter.
        if (
          evt.key === "Enter" &&
          !evt.shiftKey &&
          !evt.nativeEvent.isComposing &&
          (evt.target as HTMLElement).closest('[contenteditable="true"]') &&
          onEnter
        ) {
          evt.preventDefault();
          evt.stopPropagation();
          onEnter();
        }
      }}
      onPaste={(evt) => {
        const pastedData = evt.clipboardData.getData("Text");
        const items = pastedData
          .split("\n")
          .map((text) => text.trim())
          .filter((text) => text !== "");
        if (items.length > 0 && editorRef.current) {
          editorRef.current.chain().focus().insertContent(items[0]).run();
          onPasteMultipleCriteria(
            items.map((text, index) => {
              if (index === 0) {
                return {
                  content: JSON.stringify(editorRef.current?.getJSON()),
                  uuid,
                };
              }
              return {
                content: JSON.stringify(convertToTiptapParagraph(text)),
              };
            }),
          );
        }
      }}
    >
      {/* Read only, the criteria read as a bulleted list rather than a
          column of inputs. */}
      {!textAreaEnabled && (
        <span aria-hidden className="shrink-0 text-sm leading-5 text-gray-600">
          •
        </span>
      )}
      <TextareaWysiwyg
        ref={editorRef}
        overridePasteEvent
        placeholder={placeholder}
        editable={textAreaEnabled}
        // Read only, no paragraph margins: the first line sits level with the
        // bullet, on the same 20px line.
        className={
          textAreaEnabled
            ? "w-full bg-white px-3 py-1"
            : "w-full leading-5 [&_p]:my-0"
        }
        value={content}
        // The editor reads "editable" (and its input border) only when it's
        // created, so switching between Preview and Edit makes a new one.
        deps={[uuid, textAreaEnabled]}
        onChangeValue={onChange}
        onBlur={onBlur}
      />
      {editable && (
        <Button
          theme={buttonTheme.iconGray}
          icon={TrashIcon}
          size={ButtonSize.medium}
          // Centres on a single-line input, and stays with the first line
          // once the text wraps.
          className={classNames(
            "mt-[7px]",
            revealOnHover &&
              "opacity-0 transition-opacity group-hover/cell:opacity-100 focus:opacity-100",
          )}
          onClick={onDelete}
          disabled={disabled}
          aria-label="Delete criteria button"
        />
      )}
    </div>
  );
};

const CriteriaEditor = ({
  disabled,
  editable,
  criteria,
  onAddCriteria,
  onPasteMultipleCriteria,
  onCriteriaChange,
  onDeleteCriteria,
  addButtonText,
  matchesRoleType = true,
  roleTypeMismatchMessage,
  onBlurCriteria,
  deleteConfirmation,
  revealAddOnHover,
  emptyState,
  centerEmptyState,
  placeholder,
  activeNote,
}: {
  onAddCriteria?: () => void;
  onPasteMultipleCriteria: (
    items: ReadonlyArray<{
      uuid?: string;
      content?: string | null;
    }>,
  ) => void;
  matchesRoleType?: boolean;
  // Why criteria can't go here, shown in place of the generic line.
  roleTypeMismatchMessage?: string;
  disabled?: boolean;
  editable?: boolean;
  criteria: ReadonlyArray<{
    uuid?: string;
    content?: string | null;
  }>;
  onDeleteCriteria?: (uuid: string) => void;
  onCriteriaChange?: (uuid: string, content: string) => void;
  addButtonText?: string;
  // Called when a criterion loses focus (autosaving pages save then).
  onBlurCriteria?: () => void;
  // Asked before deleting something that has been written in. Deleting an
  // empty box never asks: there's nothing to lose.
  deleteConfirmation?: { title: string; message: string };
  // In the table the add button only shows while the cell is hovered (or the
  // button has focus), so a column of cells stays quiet to read.
  revealAddOnHover?: boolean;
  // What a cell with nothing in it says, and what its button offers to add.
  // The cell it sits in takes a different background (see the call sites).
  emptyState?: { message: string; actionText: string };
  // Centres that prompt in its cell, for a cell whose only content is the
  // prompt itself.
  centerEmptyState?: boolean;
  // Shown in an empty editor, so a blank box says what belongs in it.
  placeholder?: string;
  // Sits under whichever criterion has the cursor in it — a note about what
  // typing here changes belongs next to what's being typed. Focus on a button
  // (add, delete) doesn't count: nothing is being edited yet.
  activeNote?: ReactNode;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  // Set when the add button is clicked, so the new item's input gets focus
  // once it renders.
  const shouldFocusNewItemRef = useRef(false);

  useEffect(() => {
    if (!shouldFocusNewItemRef.current) {
      return;
    }
    shouldFocusNewItemRef.current = false;
    // Wait a tick for the new editor to mount.
    const timeout = setTimeout(() => {
      const editors = containerRef.current?.querySelectorAll<HTMLElement>(
        '[contenteditable="true"]',
      );
      editors?.[editors.length - 1]?.focus();
    }, 50);
    return () => clearTimeout(timeout);
  }, [criteria.length]);
  const textAreaEnabled = !disabled && editable && !!onCriteriaChange;
  const { ConfirmationDialog, confirm } = useConfirm(
    deleteConfirmation?.title ?? "Delete?",
    deleteConfirmation?.message ?? "",
  );

  // The confirm dialog is awaited, and a save can land meanwhile (the click
  // blurs the editor, which saves): call the handler from then, not now.
  const onDeleteCriteriaRef = useRef(onDeleteCriteria);
  onDeleteCriteriaRef.current = onDeleteCriteria;
  const handleDelete = async (criteriaItem: {
    uuid?: string;
    content?: string | null;
  }) => {
    if (!criteriaItem.uuid || !onDeleteCriteria) {
      return;
    }
    if (
      deleteConfirmation &&
      !isEmptyValue(criteriaItem.content) &&
      !(await confirm())
    ) {
      return;
    }
    onDeleteCriteriaRef.current?.(criteriaItem.uuid);
  };

  const isEmpty = criteria.length === 0;
  // Nothing here yet: the same prompt an unfilled level gets, rather than an
  // empty box with a button hidden until you hover it.
  if (isEmpty && (emptyState || !matchesRoleType)) {
    return (
      <div
        className={classNames(
          "flex flex-col gap-2",
          centerEmptyState
            ? "h-full items-center justify-center text-center"
            : "items-start text-left",
        )}
      >
        <div className="text-gray-400 text-xs">
          {matchesRoleType
            ? emptyState?.message
            : (roleTypeMismatchMessage ??
              "Criteria can't be added to this role type")}
        </div>
        {matchesRoleType && editable && onAddCriteria && emptyState && (
          <AddButton
            disabled={disabled}
            size={ButtonSize.medium}
            leftNegativeMargin={!centerEmptyState}
            text={emptyState.actionText}
            aria-label="Add criteria button"
            onClick={() => {
              shouldFocusNewItemRef.current = true;
              onAddCriteria();
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      // A bulleted list sits tighter than a column of inputs.
      className={classNames(
        "flex flex-col",
        textAreaEnabled ? "gap-2" : "gap-1.5",
      )}
    >
      <ConfirmationDialog />
      {criteria.map((criteriaItem, index) => {
        const key = criteriaItem.uuid ?? String(index);
        return (
          <div
            key={key}
            onFocusCapture={(evt) =>
              setFocusedKey(
                evt.target.closest('[contenteditable="true"]') ? key : null,
              )
            }
            onBlurCapture={(evt) => {
              // Moving to another criterion hands the note over; leaving the
              // editor altogether puts it away.
              if (!evt.currentTarget.contains(evt.relatedTarget)) {
                setFocusedKey((previous) =>
                  previous === key ? null : previous,
                );
              }
            }}
          >
            <CriteriaItem
              key={key}
              disabled={disabled}
              uuid={criteriaItem.uuid}
              content={criteriaItem.content}
              editable={editable}
              textAreaEnabled={textAreaEnabled}
              onPasteMultipleCriteria={onPasteMultipleCriteria}
              onBlur={onBlurCriteria}
              revealOnHover={revealAddOnHover}
              placeholder={placeholder}
              onEnter={
                editable && onAddCriteria
                  ? () => {
                      shouldFocusNewItemRef.current = true;
                      onAddCriteria();
                    }
                  : undefined
              }
              onDelete={() => handleDelete(criteriaItem)}
              onChange={(content) => {
                if (!criteriaItem.uuid || !onCriteriaChange) {
                  return;
                }
                onCriteriaChange(criteriaItem.uuid, content);
              }}
            />
            {focusedKey === key && activeNote}
          </div>
        );
      })}
      {editable && onAddCriteria && (
        <Button
          disabled={disabled}
          size={ButtonSize.medium}
          theme={buttonTheme.iconGray}
          className={classNames(
            "self-start",
            revealAddOnHover &&
              "opacity-0 transition-opacity group-hover/cell:opacity-100 focus:opacity-100",
          )}
          icon={TopicflowIconMap.plus}
          tooltip={addButtonText ?? "Add Criteria"}
          aria-label="Add criteria button"
          onClick={() => {
            shouldFocusNewItemRef.current = true;
            onAddCriteria();
          }}
        />
      )}
    </div>
  );
};

export default CriteriaEditor;
